// Reactive UI state. The engine writes, components read.
import { computed, signal } from "@preact/signals";
import type { Broadcast, Channel, ChatSetting, Community, Group, Privacy } from "./api";
import type { Chat, ContactProfile, Msg } from "./db";

export type ConnState = "offline" | "connecting" | "online";

export interface RosterItem {
  jid: string;
  name: string;
  subscription: "none" | "to" | "from" | "both" | "remove";
}

export interface PeerPresence {
  /** resource -> away? */
  resources: Map<string, boolean>;
  /** latest idle-since / last unavailable time we observed (ms) */
  lastActive?: number;
}

export const conn = signal<ConnState>("offline");
export const syncing = signal(false);
export const chats = signal<Chat[]>([]);
export const activeChat = signal<string | null>(null);
export const messages = signal<Msg[]>([]);
export const hasOlder = signal(true);
export const roster = signal<Map<string, RosterItem>>(new Map());
export const presence = signal<Map<string, PeerPresence>>(new Map());
export const typing = signal<Map<string, number>>(new Map()); // chat -> expiry ms
export const typingWho = signal<Map<string, string>>(new Map()); // group chat -> who is typing
export const groups = signal<Map<string, Group>>(new Map());
export const privacy = signal<Privacy>({ read_receipts: true, last_seen: "everyone", profile_photo: "everyone", about: "everyone", groups_add: "everyone" });
/** Contacts we blocked (XEP-0191; the server drops their messages/presence). */
export const blocked = signal<Set<string>>(new Set());
export const pushEnabled = signal(false);
/** Disappearing-message timers of 1:1 chats (seconds). */
export const timers = signal<Map<string, number>>(new Map());

export function chatTTL(jid: string): number {
  if (isGroup(jid)) return groups.value.get(jid)?.ttl ?? 0;
  return timers.value.get(jid) ?? 0;
}

export function ttlLabel(ttl: number): string {
  if (ttl >= 90 * 86400) return "90 days";
  if (ttl >= 7 * 86400) return "7 days";
  if (ttl >= 86400) return "24 hours";
  return "off";
}

/** Status to display: with read receipts off, 1:1 chats never show blue
 * ticks (WhatsApp rule: you don't see others' if you don't send yours). */
export function shownStatus(chat: string, status: number): number {
  if (!privacy.value.read_receipts && !isGroup(chat) && status === 3) return 2;
  return status;
}
export const mucDomain = signal("conference.xmm.local");
/** CDN base for media when the server uses one (/api/v1/config). */
export const mediaBase = signal("");
export const myJid = signal("");

/** Rooms: groups are "g…", channels "c…" (same MUC service). */
export const isGroup = (jid: string) => jid.endsWith("@" + mucDomain.value) && !jid.startsWith("c");
export const isChannel = (jid: string) => jid.startsWith("c") && jid.endsWith("@" + mucDomain.value);
/** Broadcast lists are local chats: "b-<id>@broadcast". */
export const BROADCAST = "broadcast";
export const isBroadcast = (jid: string) => jid.endsWith("@" + BROADCAST);
export const broadcastJid = (id: string) => `b-${id}@${BROADCAST}`;

// ---- phase 7 ---------------------------------------------------------------------

/** Sidebar section. */
export const tab = signal<"chats" | "status" | "channels" | "communities">("chats");

/** A decrypted status update. */
export interface StatusView {
  id: string;
  owner: string;
  kind: "text" | "image" | "video";
  text?: string;
  bg?: string;
  font?: number;
  caption?: string;
  media?: { url: string; key?: string; iv?: string; sha256?: string; mime: string; width?: number; height?: number; duration?: number; thumb?: string };
  createdAt: number;
  expiresAt: number;
  viewed: boolean;
  views?: { jid: string; at: number; reaction?: string }[];
  /** who's @mentioned in it */
  mentions?: string[];
  /** our reaction (someone else's status) */
  reaction?: string;
  failed?: boolean; // couldn't be decrypted on this device
}

/** Contacts' status updates (by owner, oldest first) and ours. */
export const statuses = signal<Map<string, StatusView[]>>(new Map());
export const myStatuses = signal<StatusView[]>([]);
export const statusPrivacy = signal<{ audience: "contacts" | "except" | "only"; list: string[] }>({ audience: "contacts", list: [] });
export const channels = signal<Map<string, Channel>>(new Map());
export const broadcasts = signal<Map<string, Broadcast>>(new Map());
export const communities = signal<Community[]>([]);

/** The open chat's pinned messages (newest pin first, at most 3). */
export const pinned = signal<import("./db").Msg[]>([]);
/** Chat lock: the locked chats (this device), whether they're unlocked right
 * now, and whether the Locked chats list is showing. */
export const lockedChats = signal<Set<string>>(new Set());
export const lockOpen = signal(false);
export const showLocked = signal(false);
/** Bumped when a group's join requests change (admins reload them). */
export const joinRequestsChanged = signal(0);
/** Phone: a computer is waiting for its linking code to be typed here. */
/** Bumped when a device was linked here (the Linked devices list reloads). */
export const devicesChanged = signal(0);
/** A new main phone: offer to restore the chats from a backup. */
export const offerRestore = signal(false);
/** Phone-book names of people on xmm (from our phone, end-to-end encrypted). */
export const contactNames = signal<Map<string, string>>(new Map());
export const linkCodeRequest = signal<{ device: string; at: number } | null>(null);
/** People and groups you message most (recently), best first. */
export const frequent = signal<string[]>([]);
/** Logging in to another account on this device (the current one stays). */
export const addingAccount = signal(false);
/** A new device waiting for / receiving the chat history. */
export const historyState = signal<"none" | "waiting" | "importing" | "done" | "declined">("none");

export function groupRole(room: string, jid: string) {
  return groups.value.get(room)?.members.find((m) => m.jid === jid)?.role;
}
export const isGroupAdmin = (room: string, jid: string) => {
  const r = groupRole(room, jid);
  return r === "owner" || r === "admin";
};
/** Can we post in this group right now? */
export function canPost(room: string): { ok: boolean; reason?: string } {
  const g = groups.value.get(room);
  if (!g || g.left || !g.my_role) return { ok: false, reason: "You can't send messages to this group because you're no longer a participant." };
  if (g.only_admins_send && g.my_role === "member") return { ok: false, reason: "Only admins can send messages." };
  return { ok: true };
}
export const lastSeen = signal<Map<string, number | null>>(new Map());
export const profiles = signal<Map<string, ContactProfile>>(new Map());
/** Upload progress 0..1 per message key. */
export const uploads = signal<Map<string, number>>(new Map());
/** Message to scroll to and highlight (search / starred / quote). */
export const focusMessage = signal<string | null>(null);
export const chatSettings = signal<Map<string, ChatSetting>>(new Map());
export const stars = signal<Set<string>>(new Set());

export function isMuted(jid: string): boolean {
  const until = chatSettings.value.get(jid)?.muted_until;
  return !!until && until * 1000 > Date.now();
}
export const isArchived = (jid: string) => !!chatSettings.value.get(jid)?.archived;
export const pinnedAt = (jid: string) => chatSettings.value.get(jid)?.pinned_at ?? 0;

/** The user is looking at the app: visible tab or focused window. Counts
 * as "viewing" (read receipts) and "online". */
export const isForeground = () => document.visibilityState === "visible" || document.hasFocus();
export const focused = signal(typeof document !== "undefined" ? isForeground() : true);
/** Actually looking at the chat: the window is visible *and* focused. Only
 * then are messages marked read (a visible window in the background, on
 * another screen or behind another app, isn't reading). */
export const isAttentive = () => document.visibilityState === "visible" && document.hasFocus();
export const attentive = signal(typeof document !== "undefined" ? isAttentive() : true);

/** Contacts are roster entries whose presence we receive. */
export const contacts = computed(() =>
  [...roster.value.values()]
    .filter((r) => r.subscription === "to" || r.subscription === "both")
    // The phone-book name (synced from our phone) wins, as in displayName.
    .map((r) => (contactNames.value.has(r.jid) ? { ...r, name: contactNames.value.get(r.jid)! } : r)),
);

export const totalUnread = computed(() => chats.value.reduce((n, c) => n + (c.unread && !isMuted(c.jid) && !isArchived(c.jid) ? 1 : 0), 0));

export function displayName(jid: string): string {
  if (jid === myJid.value) return "You";
  if (isGroup(jid)) return groups.value.get(jid)?.name ?? "Group";
  if (isChannel(jid)) return channels.value.get(jid)?.name ?? "Channel";
  if (isBroadcast(jid)) {
    const b = broadcasts.value.get(jid);
    return b ? b.name || b.members.map((m) => displayName(m.jid)).join(", ") : "Broadcast list";
  }
  // The name in our phone's address book first (synced end-to-end), like WhatsApp.
  const book = contactNames.value.get(jid);
  if (book) return book;
  const r = roster.value.get(jid);
  if (r?.name && (r.subscription === "to" || r.subscription === "both")) return r.name;
  return "+" + jid.split("@")[0];
}

/** WhatsApp shows "~ Profile Name" next to unsaved numbers. */
/** Name for a group member: saved contact name, else "~Profile name", else number. */
export function memberName(jid: string): string {
  if (jid === myJid.value) return "You";
  const r = roster.value.get(jid);
  if (r?.name && (r.subscription === "to" || r.subscription === "both")) return r.name;
  const p = profiles.value.get(jid)?.name;
  return p ? "~" + p : "+" + jid.split("@")[0];
}

export function pushName(jid: string): string | undefined {
  const r = roster.value.get(jid);
  if (r?.name && (r.subscription === "to" || r.subscription === "both")) return undefined;
  return profiles.value.get(jid)?.name || undefined;
}

export function isOnline(jid: string): boolean {
  const p = presence.value.get(jid);
  if (!p) return false;
  for (const away of p.resources.values()) if (!away) return true;
  return false;
}

export function isTyping(jid: string): boolean {
  const t = typing.value.get(jid);
  return !!t && t > Date.now();
}

/** Human text for a group system event, using this device's contact names. */
export function systemText(e: { type: string; actor: string; targets: string[]; value: string }, fallback: string): string {
  const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
  const who = (j: string) => (j === myJid.value ? "you" : memberName(j));
  const actor = e.actor.startsWith("system@") ? "" : who(e.actor);
  const list = (js: string[]) => {
    const n = js.map(who);
    return n.length <= 2 ? n.join(" and ") : `${n.slice(0, -1).join(", ")} and ${n[n.length - 1]}`;
  };
  switch (e.type) {
    case "disappearing": {
      const ttl = Number(e.value) || 0;
      return ttl
        ? cap(`${actor} turned on disappearing messages. New messages will disappear from this chat ${ttlLabel(ttl)} after they're sent.`)
        : cap(`${actor} turned off disappearing messages.`);
    }
    case "security":
      return `Your security code with ${memberName(e.actor)} changed. Tap to verify.`;
    case "created":
      return cap(`${actor} created group "${e.value}"`);
    case "added":
      return cap(`${actor} added ${list(e.targets)}`);
    case "removed":
      return cap(`${actor} removed ${list(e.targets)}`);
    case "left":
      return cap(`${actor} left`);
    case "joined":
      return cap(`${actor} joined using this group's invite link`);
    case "approved":
      return cap(`${actor} approved ${list(e.targets)}'s request to join`);
    case "pinned":
      return cap(`${actor} pinned a message`);
    case "history-shared":
      return cap(`${actor} shared ${e.value} recent messages with you`);
    case "promoted":
      return e.targets.includes(myJid.value) ? "You're now an admin" : `${list(e.targets)} is now an admin`;
    case "demoted":
      return e.targets.includes(myJid.value) ? "You're no longer an admin" : `${list(e.targets)} is no longer an admin`;
    case "name":
      return cap(`${actor} changed the group name to "${e.value}"`);
    case "description":
      return cap(`${actor} changed the group description`);
    case "photo":
      return cap(`${actor} changed this group's icon`);
    case "settings": {
      const [k, v] = e.value.split("=");
      if (k === "only_admins_send") return cap(`${actor} changed this group's settings to allow ${v === "true" ? "only admins" : "all participants"} to send messages`);
      if (k === "only_admins_edit") return cap(`${actor} changed this group's settings to allow ${v === "true" ? "only admins" : "all participants"} to edit this group's info`);
      if (k === "share_history") return cap(`${actor} turned ${v === "true" ? "on" : "off"} sharing recent messages with new participants`);
      if (k === "approve_joins") return cap(`${actor} turned ${v === "true" ? "on" : "off"} admin approval to join this group`);
      return fallback;
    }
  }
  return fallback;
}
